import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const TasksKanban = ({ user }) => {
  // Check if user is admin
  const isAdmin = user?.role?.type === 'admin' || 
                  user?.role?.name === 'Administrator' || 
                  user?.email === 'admin@velocityairportsolutions.com';
  
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState(['To Do', 'In Progress', 'Done']);
  const [showAddTask, setShowAddTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [editingStatuses, setEditingStatuses] = useState([]);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    body: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'To Do',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    deadline: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchTasks();
    fetchStatuses();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/api/tasks`);
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/api/task-statuses`);
      setStatuses(response.data.statuses);
      setEditingStatuses(response.data.statuses);
    } catch (error) {
      console.error('Failed to fetch statuses');
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // Dropped outside the list
    if (!destination) return;

    // Dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    // Move task to new status
    const newStatus = destination.droppableId;
    const taskId = draggableId;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('status', newStatus);
      
      await axios.put(`${API}/api/tasks/${taskId}`, formDataToSend);
      fetchTasks();
      toast.success('Task moved');
    } catch (error) {
      toast.error('Failed to move task');
    }
  };

  const saveStatuses = async () => {
    try {
      await axios.put(`${API}/api/task-statuses`, editingStatuses);
      setStatuses(editingStatuses);
      setShowSettings(false);
      toast.success('Statuses updated');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update statuses');
    }
  };

  const addStatus = () => {
    const newStatus = prompt('Enter new status name:');
    if (newStatus && newStatus.trim()) {
      setEditingStatuses([...editingStatuses, newStatus.trim()]);
    }
  };

  const removeStatus = (index) => {
    if (editingStatuses.length <= 1) {
      toast.error('Must have at least one status');
      return;
    }
    const newStatuses = editingStatuses.filter((_, i) => i !== index);
    setEditingStatuses(newStatuses);
  };

  const editStatus = (index, newName) => {
    const newStatuses = [...editingStatuses];
    newStatuses[index] = newName;
    setEditingStatuses(newStatuses);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedTask) {
        await axios.put(`${API}/api/tasks/${selectedTask.id}`, formDataToSend);
        toast.success('Task updated');
      } else {
        await axios.post(`${API}/api/tasks`, formDataToSend);
        toast.success('Task created');
      }

      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      status: 'To Do',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      deadline: '',
      priority: 'medium'
    });
    setShowAddTask(false);
    setSelectedTask(null);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    
    try {
      await axios.delete(`${API}/api/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleEdit = (task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      status: task.status,
      contact_name: task.contact_name || '',
      contact_email: task.contact_email || '',
      contact_phone: task.contact_phone || '',
      deadline: task.deadline ? new Date(task.deadline).toISOString().slice(0, 16) : '',
      priority: task.priority || 'medium'
    });
    setShowAddTask(true);
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('status', newStatus);
      
      await axios.put(`${API}/api/tasks/${task.id}`, formDataToSend);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const openWhatsApp = (phone, taskTitle) => {
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    const message = encodeURIComponent(`Hi! Regarding: ${taskTitle}`);
    window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const sendEmail = (email, taskTitle, taskDescription) => {
    if (!email) {
      toast.error('No email available');
      return;
    }
    // Open email composer dialog
    setEmailData({
      to: email,
      subject: `Regarding: ${taskTitle}`,
      body: taskDescription || `Hi,\n\nI'm reaching out regarding: ${taskTitle}\n\nBest regards`
    });
    setShowEmailComposer(true);
  };

  const handleSendEmail = async () => {
    try {
      const response = await axios.post(`${API}/api/send-task-email`, {
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.body
      });
      
      toast.success('Email sent successfully');
      setShowEmailComposer(false);
      setEmailData({ to: '', subject: '', body: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const getTasksByStatus = (status) => {
    return tasks.filter(task => task.status === status);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-red-500';
      case 'medium': return 'border-yellow-500';
      case 'low': return 'border-green-500';
      default: return 'border-gray-300';
    }
  };

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Tasks</h2>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowSettings(true)}>
              ⚙️ Manage Statuses
            </Button>
          )}
          <Button onClick={() => { setShowAddTask(true); setSelectedTask(null); }}>
            + New Task
          </Button>
        </div>
      </div>

      {/* Kanban Board with Drag & Drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {statuses.map(status => (
            <Droppable droppableId={status} key={status}>
              {(provided, snapshot) => (
                <Card className={snapshot.isDraggingOver ? 'bg-blue-50' : ''}>
                  <CardHeader>
                    <CardTitle className="text-lg">{status} ({getTasksByStatus(status).length})</CardTitle>
                  </CardHeader>
                  <CardContent
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-3 min-h-[200px]"
                  >
                    {getTasksByStatus(status).map((task, index) => (
                      <Draggable draggableId={task.id} index={index} key={task.id}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`p-3 border-l-4 ${getPriorityColor(task.priority)} bg-white rounded shadow-sm cursor-pointer hover:shadow-md transition ${
                              snapshot.isDragging ? 'shadow-lg rotate-2' : ''
                            }`}
                            onClick={() => handleEdit(task)}
                          >
                            <div className="flex justify-between items-start">
                              <h4 className="font-semibold">{task.title}</h4>
                              {task.priority && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  task.priority === 'high' ? 'bg-red-100 text-red-700' :
                                  task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {task.priority}
                                </span>
                              )}
                            </div>
                            {task.description && (
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{task.description}</p>
                            )}
                            {task.deadline && (
                              <p className={`text-xs mt-2 ${isOverdue(task.deadline) ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                Due: {new Date(task.deadline).toLocaleDateString()}
                                {isOverdue(task.deadline) && ' (Overdue)'}
                              </p>
                            )}
                            {task.contact_name && (
                              <div className="mt-2 text-sm text-gray-700">
                                👤 {task.contact_name}
                              </div>
                            )}
                            <div className="flex gap-2 mt-2">
                              {task.contact_phone && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 hover:text-green-700 hover:border-green-600"
                                  onClick={(e) => { e.stopPropagation(); openWhatsApp(task.contact_phone, task.title); }}
                                >
                                  📱 WhatsApp
                                </Button>
                              )}
                              {task.contact_email && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 hover:text-blue-700 hover:border-blue-600"
                                  onClick={(e) => { e.stopPropagation(); sendEmail(task.contact_email, task.title, task.description); }}
                                >
                                  ✉️ Email
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {getTasksByStatus(status).length === 0 && (
                      <p className="text-center text-gray-400 py-4">No tasks</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* Add/Edit Task Dialog */}
      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTask ? 'Edit Task' : 'New Task'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <textarea
                className="w-full border rounded p-2"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <select
                  className="w-full border rounded p-2"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  className="w-full border rounded p-2"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Deadline</Label>
              <Input
                type="datetime-local"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              />
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              {selectedTask && (
                <Button type="button" variant="destructive" onClick={() => handleDelete(selectedTask.id)}>
                  Delete
                </Button>
              )}
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit">
                {selectedTask ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Status Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Task Statuses</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Customize your task statuses. Tasks will automatically adapt to changes.</p>
            <div className="space-y-2">
              {editingStatuses.map((status, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Input
                    value={status}
                    onChange={(e) => editStatus(index, e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeStatus(index)}
                    disabled={editingStatuses.length <= 1}
                  >
                    ✕
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-between">
              <Button type="button" variant="outline" onClick={addStatus}>
                + Add Status
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowSettings(false); setEditingStatuses(statuses); }}>
                  Cancel
                </Button>
                <Button onClick={saveStatuses}>
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Composer Dialog */}
      <Dialog open={showEmailComposer} onOpenChange={setShowEmailComposer}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>To</Label>
              <Input
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                placeholder="recipient@example.com"
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <textarea
                className="w-full border rounded p-2 min-h-[200px]"
                value={emailData.body}
                onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                placeholder="Type your message here..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowEmailComposer(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail}>
                Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
